import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";
import axios, { API } from "../../lib/api";
import { chatSessionId, streamChat } from "../../lib/chat";
import { ChatBubble } from "./ChatBubble";
import { useLang } from "../../lib/i18n";

export const ChatWidget = ({ onDraft }) => {
  const { t, lang } = useLang();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const loaded = useRef(false);
  const end = useRef(null);

  useEffect(() => {
    axios.get(`${API}/chat/config`).then((r) => setEnabled(Boolean(r.data?.enabled))).catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    if (!open || loaded.current) return;
    loaded.current = true;
    axios.get(`${API}/chat/${chatSessionId()}`).then((r) => setMsgs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [msgs, open]);

  const send = async (message) => {
    const m = message.trim();
    if (!m || busy) return;
    setText("");
    setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: m }, { role: "assistant", content: "" }]);
    const append = (t) => setMsgs((x) => [...x.slice(0, -1), { role: "assistant", content: x[x.length - 1].content + t }]);
    try {
      await streamChat(chatSessionId(), m, lang, append);
    } catch (e) {
      setMsgs((x) => [...x.slice(0, -1), { role: "assistant", content: e.message }]);
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;
  const list = [{ role: "assistant", content: t("chat.hello") }, ...msgs];

  return (
    <>
      <motion.button
        data-testid="chat-open-btn"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 260, damping: 20 }}
        className="fixed bottom-5 left-5 z-50 flex items-center gap-2 rounded-full bg-[var(--teal)] text-white pl-4 pr-5 py-3.5 shadow-[0_20px_60px_-15px_rgba(15,138,120,0.8)] transition-transform hover:-translate-y-0.5"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="text-sm font-semibold">{open ? t("chat.close") : t("chat.open")}</span>
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            data-lenis-prevent
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-50 bottom-[84px] left-3 right-3 sm:left-5 sm:right-auto sm:w-[400px] h-[min(560px,calc(100svh-120px))] flex flex-col rounded-[24px] border hairline bg-[#0a1f19]/95 backdrop-blur-xl shadow-2xl overflow-hidden"
          >
            <div className="px-5 py-4 border-b hairline">
              <p className="font-display text-lg font-semibold">{t("chat.title")}</p>
              <p className="font-mono text-[11px] text-[var(--sage)]">{t("chat.note")}</p>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {list.map((m, i) => <ChatBubble key={i} index={i} role={m.role} content={m.content} onDraft={onDraft} />)}
              {msgs.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {t("chat.suggest").map((s, i) => (
                    <button key={s} data-testid={`chat-suggestion-${i}`} onClick={() => send(s)} className="rounded-full border hairline px-3 py-1.5 text-xs text-[var(--sage)] hover:text-[var(--cream)] hover:bg-white/5">{s}</button>
                  ))}
                </div>
              )}
              <div ref={end} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="p-3 border-t hairline flex gap-2">
              <input
                data-testid="chat-input"
                value={text}
                maxLength={800}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("chat.ph")}
                className="flex-1 h-11 rounded-full bg-white/5 border hairline px-4 text-sm text-[var(--cream)] placeholder:text-[var(--sage)] outline-none focus:border-[var(--mint)]"
              />
              <button data-testid="chat-send-btn" disabled={busy || !text.trim()} className="grid h-11 w-11 place-items-center rounded-full bg-[var(--husk)] text-[var(--forest)] disabled:opacity-40" aria-label={t("chat.send")}>
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
